import { useId, type CSSProperties, type ReactNode, type SVGProps } from 'react'
import type { DomainDef, IndexDef, LevelCode, ObjectiveState } from '@/api/types/readiness'
import { LEVELS, levelNum } from '@/api/types/readiness'
import { levelName } from '@/lib/indexes/registry'
import { levelToken, needsConfirmation } from '@/lib/readiness/score'
import { formatDate } from '@/components/common/format'

// ── Icons: inline stroke SVG, 2px, 14–18px, no emoji (§4.1) ─────────────────

type IconName =
  | 'check' | 'info' | 'warn' | 'globe' | 'chevron' | 'sync' | 'camera' | 'download' | 'arrow-left'
  | 'plus' | 'link' | 'calendar' | 'user' | 'search' | 'file' | 'external' | 'x' | 'clock' | 'arrow-right' | 'history'
  | 'list' | 'grid' | 'trend' | 'chevron-left' | 'chevron-right' | 'save' | 'upload'

const PATHS: Record<IconName, ReactNode> = {
  check: <path d="M20 6 9 17l-5-5" />,
  info: (<><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></>),
  warn: (<><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></>),
  globe: (<><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" /></>),
  chevron: <path d="m6 9 6 6 6-6" />,
  sync: (<><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" /><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" /><path d="M21 3v5h-5M3 21v-5h5" /></>),
  camera: (<><path d="M4 7h3l2-3h6l2 3h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" /><circle cx="12" cy="13" r="3.5" /></>),
  download: (<><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M4 21h16" /></>),
  'arrow-left': (<><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></>),
  'arrow-right': (<><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>),
  plus: <path d="M12 5v14M5 12h14" />,
  link: (<><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></>),
  calendar: (<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 11h18" /></>),
  user: (<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>),
  search: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
  file: (<><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /></>),
  external: (<><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5" /></>),
  x: <path d="M18 6 6 18M6 6l12 12" />,
  clock: (<><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>),
  history: (<><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l3 3" /></>),
  list: (<><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></>),
  grid: (<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>),
  trend: (<><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></>),
  'chevron-left': <path d="m15 18-6-6 6-6" />,
  'chevron-right': <path d="m9 18 6-6-6-6" />,
  save: (<><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" /><path d="M17 21v-8H7v8M7 3v5h8" /></>),
  upload: (<><path d="M12 17V5" /><path d="m7 10 5-5 5 5" /><path d="M4 21h16" /></>),
}

export function Icon({ name, size = 16, ...rest }: { name: IconName; size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" {...rest}>
      {PATHS[name]}
    </svg>
  )
}

// ── Chips ───────────────────────────────────────────────────────────────────

export type ChipTone = 'ok' | 'warn' | 'info' | 'tile' | 'chip' | 'dashed' | 'neutral'

const TONES: Record<ChipTone, CSSProperties> = {
  ok: { background: 'var(--rd-status-ok-bg)', color: 'var(--rd-status-ok-text)' },
  warn: { background: 'var(--rd-status-warn-bg)', color: 'var(--rd-status-warn)' },
  info: { background: 'var(--rd-accent-chip-bg)', color: 'var(--rd-status-info)' },
  tile: { background: 'var(--rd-bg-tile)', color: 'var(--rd-text-secondary)' },
  chip: { background: 'var(--rd-accent-chip-bg)', color: 'var(--rd-accent-chip-fg)', fontWeight: 600, letterSpacing: '0.06em' },
  dashed: { background: 'transparent', color: 'var(--rd-text-muted)', border: '1px dashed var(--rd-border-control)' },
  neutral: { background: 'var(--rd-bg-tile)', color: 'var(--rd-text-muted)' },
}

export function Chip({ tone = 'tile', children, style, title, className = '' }: { tone?: ChipTone; children: ReactNode; style?: CSSProperties; title?: string; className?: string }) {
  return (
    <span className={`rd-chip ${className}`} style={{ ...TONES[tone], ...style }} title={title}>
      {children}
    </span>
  )
}

export function LevelChip({ index, level, tint }: { index: IndexDef; level: LevelCode; tint?: string }) {
  return (
    <Chip tone="tile" style={tint ? { background: `${tint}22`, color: tint } : { color: levelToken(level) }}>
      <span className="rd-mono">{level}</span> {levelName(index, level)}
    </Chip>
  )
}

// Provenance chip row for an objective (§6.3).
export function ProvenanceChips({ index, state, evidenceCount, plansInProgress, sources = [] }: { index: IndexDef; state: ObjectiveState | undefined; evidenceCount: number; plansInProgress?: number; sources?: string[] }) {
  const chips: ReactNode[] = []
  if (!state || state.status === 'unset') {
    chips.push(<Chip key="pending" tone="dashed">Manual input pending</Chip>)
  } else if (state.status === 'suggested') {
    const l = state.suggested_level ?? state.level
    chips.push(
      <Chip key="sug" tone="warn" title={state.suggested_reason ?? undefined}>
        <Icon name="info" size={12} /> Suggested {l} · needs confirmation
      </Chip>
    )
  } else {
    // Provenance follows the evidence behind the level: Discovery (auto-detected),
    // Playbook (derived) or a user's declaration (§3.3 labels).
    if (sources.includes('discovery')) chips.push(<Chip key="auto" tone="ok"><Icon name="check" size={12} /> Auto-detected · Discovery</Chip>)
    if (sources.includes('playbook')) chips.push(<Chip key="derived" tone="ok"><Icon name="check" size={12} /> Derived · Playbook</Chip>)
    // A served cell carries no set date (only its drawer detail does): no dangling dash.
    if (state.status === 'declared') chips.push(<Chip key="man" tone="tile">Manual · declared{state.set_at ? ` ${formatDate(state.set_at)}` : ''}</Chip>)
    if (state.status === 'confirmed' && state.set_by) chips.push(<Chip key="by" tone="tile">Confirmed by {state.set_by} · {formatDate(state.set_at)}</Chip>)
    if (needsConfirmation(state) && state.suggested_level)
      chips.push(
        <Chip key="newsug" tone="warn" title={state.suggested_reason ?? undefined}>
          <Icon name="info" size={12} /> Confirmed {state.level} · system now suggests {state.suggested_level}
        </Chip>
      )
  }
  chips.push(<Chip key="ev" tone="tile">{evidenceCount} evidence</Chip>)
  if (state?.due_at) chips.push(<Chip key="due" tone="tile"><Icon name="calendar" size={12} /> Due {formatDate(state.due_at)}</Chip>)
  if (plansInProgress) chips.push(<Chip key="plans" tone="info">{plansInProgress} {plansInProgress === 1 ? 'plan' : 'plans'} in progress</Chip>)
  void index
  return <div className="flex flex-wrap items-center gap-1.5">{chips}</div>
}

// ── Charts: ring, distribution bar, pips, stepper (§6.2, §6.3) ──────────────

// Ring fills are two-tone gradients within the band's hue (design styles §1):
// grey, amber, green or the accent cyan. Any other colour is drawn solid.
const RING_GRADIENTS: Record<string, [string, string]> = {
  'var(--rd-status-neutral)': ['#64748B', '#3B4759'],
  'var(--rd-level-l0)': ['#64748B', '#3B4759'],
  'var(--rd-status-warn)': ['#FCD34D', '#D97706'],
  'var(--rd-level-l1)': ['#FCD34D', '#D97706'],
  'var(--rd-status-ok)': ['#6EE7A0', '#16A34A'],
  'var(--rd-level-l2)': ['#6EE7A0', '#16A34A'],
  'var(--rd-status-info)': ['#5BEBFF', '#0AA8DE'],
  'var(--rd-level-l3)': ['#5BEBFF', '#0AA8DE'],
  'var(--rd-accent-primary)': ['#5BEBFF', '#0AA8DE'],
}

export function ScoreRing({ score, color, size = 104, stroke = 10, children }: { score: number; color: string; size?: number; stroke?: number; children?: ReactNode }) {
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const pct = Math.max(0, Math.min(100, score))
  const gradientId = useId()
  const gradient = RING_GRADIENTS[color]
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Readiness score ${score} of 100`}>
        {gradient && (
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={gradient[0]} />
              <stop offset="100%" stopColor={gradient[1]} />
            </linearGradient>
          </defs>
        )}
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--rd-bg-tile)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={gradient ? `url(#${gradientId})` : color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * c} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">{children}</div>
    </div>
  )
}

export function DistributionBar({ distribution, total, height = 12 }: { distribution: Record<LevelCode, number>; total: number; height?: number }) {
  return (
    <div className="flex w-full" style={{ height, gap: 2 }} role="img" aria-label={LEVELS.map((l) => `${l}: ${distribution[l]}`).join(', ')}>
      {LEVELS.map((l) =>
        distribution[l] > 0 ? (
          <div key={l} style={{ flex: distribution[l], background: levelToken(l), borderRadius: 3 }} title={`${l} · ${distribution[l]} of ${total}`} />
        ) : null
      )}
      {total === 0 && <div style={{ flex: 1, background: 'var(--rd-bg-tile)', borderRadius: 3 }} />}
    </div>
  )
}

// Three-segment pip strip: the domain's two objectives summed, segments lit for each level step reached.
export function DomainPips({ levels, color }: { levels: LevelCode[]; color: string }) {
  const sum = levels.reduce((a, l) => a + levelNum(l), 0)
  const max = 3 * Math.max(1, levels.length)
  const lit = Math.round((sum / max) * 3)
  return (
    <div className="flex" style={{ gap: 3 }} aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span key={i} style={{ width: 14, height: 6, borderRadius: 3, background: i < lit ? color : 'var(--rd-border-control)' }} />
      ))}
    </div>
  )
}

export function Stepper({ level, color, status }: { level: LevelCode; color: string; status?: ObjectiveState['status'] }) {
  const n = levelNum(level)
  const unset = status === 'unset' || !status
  return (
    <div>
      <div className="flex" style={{ gap: 4 }} aria-hidden="true">
        {LEVELS.map((l, i) => {
          const filled = i <= n && !(unset && i > 0)
          const bg = i === 0 && n === 0 ? 'var(--rd-status-neutral)' : filled ? color : 'var(--rd-border-control)'
          return <span key={l} style={{ flex: 1, height: 8, borderRadius: 4, background: bg }} />
        })}
      </div>
      <div className="flex mt-1" style={{ gap: 4 }}>
        {LEVELS.map((l) => (
          <span key={l} className="rd-mono" style={{ flex: 1, fontSize: 11, textAlign: 'center', color: l === level ? 'var(--rd-text-primary)' : 'var(--rd-text-faint)', fontWeight: l === level ? 600 : 400 }}>
            {l}
          </span>
        ))}
      </div>
    </div>
  )
}

export function DomainDot({ domain, theme, size = 12 }: { domain: DomainDef; theme: 'light' | 'dark'; size?: number }) {
  return <span className="inline-block shrink-0 rounded-full" style={{ width: size, height: size, background: theme === 'dark' ? domain.color_dark : domain.color_light }} aria-hidden="true" />
}

export function SectionLabel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <p className={`rd-label ${className}`}>{children}</p>
}

export function Skeleton({ height = 120, className = '' }: { height?: number; className?: string }) {
  return <div className={`rd-skeleton ${className}`} style={{ height }} aria-hidden="true" />
}

// ── Scale score_0_10 (I2 §11.2): ten-segment bar with the score in mono and a
// sector tick ─────────────────────────────────────────────────────────────────
export function ScoreBar({ score, color, sector, suggested, height = 8 }: { score: number | null; color: string; sector?: number | null; suggested?: boolean; height?: number }) {
  const filled = score == null ? 0 : Math.round(score)
  return (
    <div>
      <div className="relative" style={{ height: height + 6 }}>
        <div className="absolute inset-x-0 top-[3px] flex" style={{ gap: 3, height }} aria-hidden="true">
          {Array.from({ length: 10 }, (_, i) => (
            <span key={i} style={{ flex: 1, height, borderRadius: 3, background: i < filled ? color : 'var(--rd-border-control)', opacity: suggested && i < filled ? 0.55 : 1 }} />
          ))}
        </div>
        {sector != null && (
          <span className="absolute top-0" style={{ left: `calc(${(100 * sector) / 10}% - 1px)`, width: 2, height: height + 6, background: 'var(--rd-text-secondary)', borderRadius: 1 }} title={`Sector mean ${sector.toFixed(1)}`} aria-hidden="true" />
        )}
      </div>
      <div className="mt-1 flex justify-between text-[11px]" style={{ color: 'var(--rd-text-faint)' }}>
        <span className="rd-mono">0</span>
        {sector != null && <span>sector {sector.toFixed(1)}</span>}
        <span className="rd-mono">10</span>
      </div>
    </div>
  )
}

// Inline SVG flag from the index definition (I2 §12, Note 8). Decorative; the
// text beside it remains the accessible name.
export function Flag({ svg, className = '' }: { svg: string; className?: string }) {
  return <span className={`inline-block shrink-0 overflow-hidden rounded-[2px] leading-none ${className}`} style={{ width: 20, height: 14 }} aria-hidden="true" dangerouslySetInnerHTML={{ __html: svg }} />
}

// List cap (Iteration 4 §6): the first `cap` items, then `+n more` expanding in
// place and `Show fewer` collapsing. Nothing is removed.
export function MoreToggle({ total, cap, open, onToggle, testId }: { total: number; cap: number; open: boolean; onToggle: () => void; testId?: string }) {
  if (total <= cap) return null
  return (
    <button type="button" className="rd-link mt-2 text-[12.5px]" onClick={onToggle} aria-expanded={open} data-testid={testId}>
      {open ? 'Show fewer' : `+${total - cap} more`}
    </button>
  )
}
